/**
 * Register Strategy Dialog
 *
 * Allows users to register an existing strategy from a snapshot file.
 * The snapshot must exist at: data/{trader_name}/strategies/{first_two_chars}/{strategy_id}.json
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { traderTheme as theme } from './traderDashboardTheme';

export interface RegisterStrategyDialogProps {
  open: boolean;
  traderName: string;
  onClose: () => void;
  onConfirm: (strategyId: string) => Promise<void>;
  loading: boolean;
  error: string | null;
}

// UUID v4 format regex
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const RegisterStrategyDialog: React.FC<RegisterStrategyDialogProps> = ({
  open,
  traderName,
  onClose,
  onConfirm,
  loading,
  error,
}) => {
  const { t } = useTranslation();
  const [strategyId, setStrategyId] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();
    setStrategyId(value);

    // Clear validation error when user types
    if (validationError) {
      setValidationError(null);
    }
  }, [validationError]);

  const handleConfirm = useCallback(async () => {
    // Validate UUID format
    if (!strategyId) {
      setValidationError(t('traderDashboard.registerStrategy.errors.empty'));
      return;
    }

    if (!UUID_REGEX.test(strategyId)) {
      setValidationError(t('traderDashboard.registerStrategy.errors.invalidFormat'));
      return;
    }

    await onConfirm(strategyId);
  }, [strategyId, onConfirm, t]);

  const handleClose = useCallback(() => {
    setStrategyId('');
    setValidationError(null);
    onClose();
  }, [onClose]);

  // Get the expected path for display
  const expectedPath = strategyId.length >= 2
    ? `data/${traderName}/strategies/${strategyId.substring(0, 2)}/${strategyId}.json`
    : `data/${traderName}/strategies/{prefix}/{id}.json`;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: theme.bg.primary,
          borderBottom: `1px solid ${theme.border}`,
          color: theme.accent,
        }}
      >
        {t('traderDashboard.registerStrategy.title')}
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: theme.bg.primary, pt: 3 }}>
        <Typography variant="body1" sx={{ mb: 2, color: theme.text.primary }}>
          {t('traderDashboard.registerStrategy.description')}
        </Typography>

        <TextField
          fullWidth
          label={t('traderDashboard.registerStrategy.strategyIdLabel')}
          placeholder="cd08818d-799c-42de-ab8d-9ef525f2e4aa"
          value={strategyId}
          onChange={handleChange}
          error={!!validationError}
          helperText={validationError}
          disabled={loading}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: theme.bg.secondary,
              '& fieldset': {
                borderColor: theme.border,
              },
              '&:hover fieldset': {
                borderColor: theme.accent,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.accent,
              },
            },
            '& .MuiInputLabel-root': {
              color: theme.text.secondary,
              '&.Mui-focused': {
                color: theme.accent,
              },
            },
            '& .MuiOutlinedInput-input': {
              color: theme.text.primary,
              fontFamily: 'monospace',
            },
          }}
        />

        <Box
          sx={{
            p: 2,
            backgroundColor: theme.bg.secondary,
            border: `1px solid ${theme.border}`,
            borderRadius: 1,
            mb: 2,
          }}
        >
          <Typography variant="caption" sx={{ color: theme.text.muted, display: 'block', mb: 1 }}>
            {t('traderDashboard.registerStrategy.expectedPath')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.text.secondary,
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              wordBreak: 'break-all',
            }}
          >
            {expectedPath}
          </Typography>
        </Box>

        <Box component="ul" sx={{ color: theme.text.secondary, pl: 2, mb: 0, mt: 2 }}>
          <li>
            <Typography variant="body2">
              {t('traderDashboard.registerStrategy.notes.terminal')}
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              {t('traderDashboard.registerStrategy.notes.nonTerminal')}
            </Typography>
          </li>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2, backgroundColor: 'rgba(210, 61, 61, 0.1)', color: theme.danger }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          backgroundColor: theme.bg.primary,
          borderTop: `1px solid ${theme.border}`,
          p: 2,
        }}
      >
        <Button onClick={handleClose} sx={{ color: theme.text.secondary }} disabled={loading}>
          {t('traderDashboard.registerStrategy.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || !strategyId}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: theme.text.primary }} /> : null}
          sx={{
            backgroundColor: theme.accent,
            color: theme.bg.primary,
            '&:hover': {
              backgroundColor: theme.profit,
            },
            '&.Mui-disabled': {
              backgroundColor: theme.bg.secondary,
              color: theme.text.muted,
            },
          }}
        >
          {loading ? t('traderDashboard.registerStrategy.registering') : t('traderDashboard.registerStrategy.register')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegisterStrategyDialog;
